import React, { useState } from 'react';
import { Box, FormControlLabel, Switch, Typography, IconButton, Dialog, useTheme } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { tokens } from '../../../theme';
import TagSetupGuide from './TagSetupGuide'; // Adjust the import path as necessary

const CodeDisplay = ({ organization, isDiscreteMode, colors }) => {
    const [isTagManager, setIsTagManager] = useState(false);
    const [showURLParams, setShowURLParams] = useState(false);
    const [viewRawTagURL, setViewRawTagURL] = useState(false);
    const [openGuide, setOpenGuide] = useState(false);

    const handleToggle = (event, checked) => {
        switch (event.target.name) {
            case 'isTagManager':
                setIsTagManager(checked);
                break;
            case 'showURLParams':
                setShowURLParams(checked);
                break;
            case 'viewRawTagURL':
                setViewRawTagURL(checked);
                break;
            default:
                break;
        }
    };

    const handleOpenGuide = () => {
        setOpenGuide(true);
    };

    const handleCloseGuide = () => {
        setOpenGuide(false);
    };

    const getUrlParams = () => `&puid=\${partnerUserId}&hem=\${hemSHA256}&r=\${urlToRedirectTo}`;

    const jsContainerCode = organization.hem_contribution_tag
        ? `var cs = document.createElement('script');
cs.src = "https://a.usbrowserspeed.com/cs?pid=${organization.pid}${showURLParams ? getUrlParams() : ''}";
cs.type = 'text/javascript';
document.body.appendChild(cs);

var av = document.createElement('script');
av.src = 'https://a.usbrowserspeed.com/metrics.js?pid=${organization.pid}';
av.type = 'text/javascript';
document.body.appendChild(av);`
        : `var cookie_sync = document.createElement('script');
cookie_sync.src = "https://a.usbrowserspeed.com/cs?pid=${organization.pid}${showURLParams ? getUrlParams() : ''}";
cookie_sync.type = 'text/javascript';
document.body.appendChild(cookie_sync);`;

    const tagManagerCodeBase = `<script type="text/javascript" src="https://a.usbrowserspeed.com/cs?pid=${organization.pid}"></script>`;
    const tagManagerCodeMetrics = `<script type="text/javascript" src="https://a.usbrowserspeed.com/metrics.js?pid=${organization.pid}"></script>`;
    const tagManagerCode = organization.hem_contribution_tag
        ? `${tagManagerCodeBase}\n${tagManagerCodeMetrics}`
        : tagManagerCodeBase;

    const customStyle = {
        hljs: {
            display: 'block',
            overflowX: 'auto',
            padding: '0.5em',
            background: colors.grey[900],
            color: colors.grey[100]
        },
        'hljs-keyword': {
            color: colors.primary[500],
            fontWeight: 'bold'
        },
        'hljs-string': {
            color: colors.primary[300]
        },
        'hljs-number': {
            color: colors.primary[700]
        },
        'hljs-literal': {
            color: colors.primary[600]
        },
        'hljs-comment': {
            color: colors.grey[500]
        },
        'hljs-variable': {
            color: colors.primary[200]
        },
        'hljs-operator': {
            color: colors.primary[800]
        }
    };

    return (
        <Box sx={{ p: 1, minHeight: '33%', borderRadius: '20px', backgroundColor: colors.grey[600], boxShadow: '0px 0px 10px rgba(0, 0, 0, 1)' }}>
            <Box p={2} style={{ maxWidth: "100%", overflowX: "auto" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h5" color={colors.grey[100]} style={{
                        whiteSpace: 'pre-wrap',
                        maxWidth: '80%',
                        wordWrap: 'break-word',
                    }}>
                        Organization PID: {isDiscreteMode
                            ? <Box component="span" width="80%" height="20px" bgcolor="white" />
                            : organization.pid}
                    </Typography>
                    <IconButton onClick={handleOpenGuide} sx={{ color: colors.grey[100] }}>
                        <InfoIcon />
                    </IconButton>
                </Box>
                <FormControlLabel
                    control={<Switch checked={isTagManager} onChange={(e) => handleToggle(e, e.target.checked)} name="isTagManager" />}
                    label="Tag Manager"
                    sx={{ color: colors.grey[100] }}
                />
                <FormControlLabel
                    control={<Switch checked={showURLParams} onChange={(e) => handleToggle(e, e.target.checked)} name="showURLParams" />}
                    label="Show URL Params"
                    sx={{ color: colors.grey[100] }}
                />
                <FormControlLabel
                    control={<Switch checked={viewRawTagURL} onChange={(e) => handleToggle(e, e.target.checked)} name="viewRawTagURL" />}
                    label="View Raw Tag URL"
                    sx={{ color: colors.grey[100], variant: "h4" }}
                />
                <Typography variant="h5" color={colors.primary[100]}>Sample Implementation</Typography>
                <SyntaxHighlighter language="javascript" style={customStyle}>
                    {isTagManager ? tagManagerCode : jsContainerCode}
                </SyntaxHighlighter>
                {viewRawTagURL && (
                    <>
                        <Typography variant="h6" color={colors.primary[100]}>Raw Tag Source URL</Typography>
                        <SyntaxHighlighter language="javascript" style={customStyle}>
                            {`https://a.usbrowserspeed.com/cs?pid=${organization.pid}`}
                        </SyntaxHighlighter>
                    </>
                )}
            </Box>

            <Dialog open={openGuide} onClose={handleCloseGuide} PaperProps={{ style: { borderRadius: '20px', backgroundColor: colors.primary[700], boxShadow: '0px 0px 10px rgba(0, 0, 0, 1)' } }}>
                <TagSetupGuide onClose={handleCloseGuide} organization={organization} />
            </Dialog>
        </Box>
    );
};

export default CodeDisplay;
